import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import ToolPrepPopup from "../../../shared/Popup/ToolPrepPopup";
import ToolPrepDownloadFlowModals from "../../../shared/Popup/ToolPrepDownloadFlowModals";
import ToolPrepFlatResultStage from "../../../shared/Popup/ToolPrepFlatResultStage";
import { buildToolPrepHowToUse } from "../../../shared/Popup/ToolPrepHowToUseModal";
import ToolPrepSourceCards from "../../../shared/ToolPrepSourceCards";
import BlurPreview from "./BlurPreview";
import BlurPrepControls from "./BlurPrepControls";
import ImagePickerModal from "../../../../../components/popups/imagePicker/ImagePickerModal";
import { useToolPrepImageSource } from "../../../hooks/useToolPrepImageSource";
import { useSyncScrollbarWidth } from "../../../hooks/useSyncScrollbarWidth";
import { useToolPrepBakedApplyFlow } from "../../../hooks/useToolPrepBakedApplyFlow";
import { ensureBackgroundRemoved } from "../../../../../utils/ensureBackgroundRemoved";
import { exportBlurPrepImageBlob } from "../../../lib/exportToolPrepEffectToPngBlob";
import { hasActiveBlurSettings } from "../../../lib/toolPrepEffectActiveSettings";
import {
  blurTargetNeedsCutout,
  clampBlurAmount,
  clampPixelParam,
  createDefaultBlurSettings,
  resolveBlurTarget,
  resolveBlurToolVariant,
  resolveBlurType,
  resolvePixelShape,
} from "../../../lib/blurPrepModel";
import {
  TOOL_PREP_PREPARING_PREVIEW_LABEL,
  TOOL_PREP_RESULT_FOOTER_HINT,
} from "../../../../../utils/editorEntrySession";
import {
  TOOL_PREP_CUTOUT_REVEAL_MS,
  useDelayedImageReady,
  useImageNaturalSize,
} from "../../../lib/toolPrepImageLayout";
import "./BlurToolPrep.css";

/**
 * Shared Blur prep for catalog tools:
 * blur | gaussian_blur | pixel_blur | motion_blur | bokeh_blur | background_blur
 *
 * - No cutout → blur entire image
 * - Remove BG / cutout → blur object only, keep scene sharp
 * - background_blur → always blur background, keep subject sharp
 */
const BlurToolPrep = ({
  toolKey = "blur",
  originalImageUrl = null,
  imageFile = null,
  removeBgDefault = false,
  cachedCutoutUrl = null,
  onCutoutReady = null,
  onDone = () => {},
  onCancel = () => {},
}) => {
  const variant = useMemo(() => resolveBlurToolVariant(toolKey), [toolKey]);
  const showGaussianControls = Boolean(variant.showGaussianControls);
  const lockBlurTarget = variant.lockBlurTarget
    ? resolveBlurTarget(variant.lockBlurTarget)
    : null;
  const lockBackgroundTarget = lockBlurTarget === "background";
  const { getToken } = useAuth();
  const [blurSettings, setBlurSettings] = useState(() =>
    createDefaultBlurSettings(toolKey)
  );
  const [selectedKey, setSelectedKey] = useState("preview");
  const [removeBg, setRemoveBg] = useState(() => {
    if (lockBackgroundTarget) return true;
    if (showGaussianControls) {
      return blurTargetNeedsCutout(
        createDefaultBlurSettings(toolKey).blurTarget
      );
    }
    return Boolean(removeBgDefault) || Boolean(cachedCutoutUrl);
  });
  const [cutoutUrl, setCutoutUrl] = useState(cachedCutoutUrl || null);
  const [creationId, setCreationId] = useState(null);
  const [bgLoading, setBgLoading] = useState(false);
  const [bgError, setBgError] = useState(null);
  const [doneBusy, setDoneBusy] = useState(false);
  const scrollBodyRef = useRef(null);
  const resetAppliedRef = useRef(() => {});
  const autoCutoutAttemptRef = useRef(null);

  const thankYouCopy = useMemo(
    () => ({
      titleDownloading: "Downloading…",
      titleComplete: "Download complete",
      leadDownloading: "Please wait — we’re saving your file to this device.",
      leadComplete:
        "Your file is saved on this computer or phone. You can find it in your Downloads folder.",
      previewAlt: "Blurred image download",
      primaryActionLabel: "Add new image",
    }),
    []
  );

  useEffect(() => {
    const next = createDefaultBlurSettings(toolKey);
    setBlurSettings(next);
    const nextVariant = resolveBlurToolVariant(toolKey);
    if (nextVariant.lockBlurTarget === "background") {
      setRemoveBg(true);
    } else if (nextVariant.showGaussianControls) {
      setRemoveBg(blurTargetNeedsCutout(next.blurTarget));
    }
  }, [toolKey]);

  const blurTarget = resolveBlurTarget(
    lockBlurTarget || blurSettings.blurTarget
  );
  const needsCutout = lockBackgroundTarget
    ? true
    : showGaussianControls
      ? blurTargetNeedsCutout(blurTarget)
      : removeBg;

  const {
    sourceImageUrl,
    resolvedFile,
    sourceFileName,
    sourceFileSizeLabel,
    clearSource,
    openUploadPicker,
    imagePickerProps,
    uploadProgress,
  } = useToolPrepImageSource({
    originalImageUrl,
    imageFile,
    busy: bgLoading || doneBusy,
    onSourceChange: () => {
      setCutoutUrl(null);
      setCreationId(null);
      setBgError(null);
      setRemoveBg(lockBackgroundTarget);
      autoCutoutAttemptRef.current = null;
      resetAppliedRef.current();
      setBlurSettings(createDefaultBlurSettings(toolKey));
      setSelectedKey("preview");
    },
  });

  const {
    applied: blurApplied,
    busy: blurBusy,
    resultUrl: blurPreviewUrl,
    applyError: blurApplyError,
    setApplyError: setBlurApplyError,
    apply: applyBlur,
    resetApplied,
    downloadBusy,
    handleDownload,
    downloadFlow,
  } = useToolPrepBakedApplyFlow({
    sourceFileName,
    fallbackDownloadName: variant.downloadName,
    toolKey: variant.toolKey,
    shareTitle: variant.shareTitle,
    shareText: variant.shareText,
    thankYouCopy,
  });

  resetAppliedRef.current = resetApplied;

  useEffect(() => {
    if (cachedCutoutUrl) {
      setCutoutUrl(cachedCutoutUrl);
      if (removeBgDefault || lockBackgroundTarget) setRemoveBg(true);
    }
  }, [cachedCutoutUrl, removeBgDefault, lockBackgroundTarget]);

  useEffect(() => {
    if (blurApplied && blurPreviewUrl) {
      setSelectedKey("preview");
    }
  }, [blurApplied, blurPreviewUrl]);

  const runBackgroundRemoval = useCallback(async () => {
    if (cutoutUrl?.startsWith("blob:") || cutoutUrl?.startsWith("data:")) {
      return { cutoutUrl, creationId };
    }
    if (
      cachedCutoutUrl?.startsWith("blob:") ||
      cachedCutoutUrl?.startsWith("data:")
    ) {
      setCutoutUrl(cachedCutoutUrl);
      return { cutoutUrl: cachedCutoutUrl, creationId };
    }

    setBgLoading(true);
    setBgError(null);
    try {
      const token = await getToken();
      const { cutoutUrl: nextUrl, creationId: cid } =
        await ensureBackgroundRemoved({
          token,
          imageFile: resolvedFile,
          imageUrl: sourceImageUrl,
          cachedCutoutUrl: cutoutUrl || cachedCutoutUrl,
        });
      setCutoutUrl(nextUrl);
      if (cid != null) setCreationId(cid);
      onCutoutReady?.(nextUrl);
      return { cutoutUrl: nextUrl, creationId: cid ?? creationId };
    } catch (err) {
      setBgError(err?.message || "Failed to remove background");
      throw err;
    } finally {
      setBgLoading(false);
    }
  }, [
    cachedCutoutUrl,
    creationId,
    cutoutUrl,
    getToken,
    onCutoutReady,
    resolvedFile,
    sourceImageUrl,
  ]);

  // Background Blur: auto-separate subject on upload.
  useEffect(() => {
    if (!lockBackgroundTarget) return;
    if (!sourceImageUrl || cutoutUrl || bgLoading || blurApplied) return;
    if (autoCutoutAttemptRef.current === sourceImageUrl) return;
    autoCutoutAttemptRef.current = sourceImageUrl;
    void runBackgroundRemoval().catch(() => {
      setBgError("Failed to remove background");
    });
  }, [
    bgLoading,
    blurApplied,
    cutoutUrl,
    lockBackgroundTarget,
    runBackgroundRemoval,
    sourceImageUrl,
  ]);

  const handleRemoveBgToggle = async () => {
    if (!sourceImageUrl || bgLoading || blurApplied) return;
    const next = !removeBg;
    resetApplied();
    setBlurApplyError(null);
    setSelectedKey("preview");
    setRemoveBg(next);
    if (!next) {
      setBgError(null);
      return;
    }
    try {
      await runBackgroundRemoval();
    } catch {
      setRemoveBg(false);
    }
  };

  const activeCutoutUrl = needsCutout ? cutoutUrl : null;
  const cutoutRevealReady = useDelayedImageReady(
    activeCutoutUrl,
    TOOL_PREP_CUTOUT_REVEAL_MS
  );
  const revealCutoutUrl =
    needsCutout && cutoutRevealReady ? activeCutoutUrl : null;
  const waitingOnCutout =
    needsCutout && Boolean(sourceImageUrl) && !revealCutoutUrl;
  const frameNaturalSize = useImageNaturalSize(sourceImageUrl);

  const handleSettingsChange = (patch) => {
    resetApplied();
    setBlurApplyError(null);
    setSelectedKey("preview");

    setBlurSettings((prev) => ({
      ...prev,
      ...patch,
      type:
        patch.type != null
          ? resolveBlurType(patch.type, variant.allowedTypes)
          : prev.type,
      amount:
        patch.amount != null ? clampBlurAmount(patch.amount) : prev.amount,
      pixelShape:
        patch.pixelShape != null
          ? resolvePixelShape(patch.pixelShape)
          : prev.pixelShape,
      pixelGap:
        patch.pixelGap != null
          ? clampPixelParam(patch.pixelGap, prev.pixelGap)
          : prev.pixelGap,
      pixelSoftness:
        patch.pixelSoftness != null
          ? clampPixelParam(patch.pixelSoftness, prev.pixelSoftness)
          : prev.pixelSoftness,
      pixelMix:
        patch.pixelMix != null
          ? clampPixelParam(patch.pixelMix, prev.pixelMix)
          : prev.pixelMix,
      blurTarget:
        lockBlurTarget ||
        (patch.blurTarget != null
          ? resolveBlurTarget(patch.blurTarget)
          : prev.blurTarget),
      gaussianMix:
        patch.gaussianMix != null
          ? clampPixelParam(patch.gaussianMix, prev.gaussianMix)
          : prev.gaussianMix,
      edgePreserve:
        patch.edgePreserve != null
          ? clampPixelParam(patch.edgePreserve, prev.edgePreserve)
          : prev.edgePreserve,
    }));

    if (showGaussianControls && patch.blurTarget != null) {
      const need = blurTargetNeedsCutout(patch.blurTarget);
      setRemoveBg(need);
      if (need && sourceImageUrl) {
        void runBackgroundRemoval().catch(() => {
          setBlurSettings((p) => ({ ...p, blurTarget: "full" }));
          setRemoveBg(false);
          setBgError("Failed to remove background");
        });
      } else if (!need) {
        setBgError(null);
      }
    }
  };

  const pixelExportFields = useMemo(
    () => ({
      pixelShape: resolvePixelShape(blurSettings.pixelShape),
      pixelGap: clampPixelParam(blurSettings.pixelGap, 0),
      pixelSoftness: clampPixelParam(blurSettings.pixelSoftness, 0),
      pixelMix: clampPixelParam(blurSettings.pixelMix, 100),
    }),
    [
      blurSettings.pixelGap,
      blurSettings.pixelMix,
      blurSettings.pixelShape,
      blurSettings.pixelSoftness,
    ]
  );

  const gaussianExportFields = useMemo(
    () => ({
      blurTarget: lockBlurTarget
        ? lockBlurTarget
        : showGaussianControls
          ? resolveBlurTarget(blurSettings.blurTarget)
          : needsCutout
            ? "subject"
            : "full",
      gaussianMix: clampPixelParam(blurSettings.gaussianMix, 100),
      edgePreserve: clampPixelParam(blurSettings.edgePreserve, 0),
    }),
    [
      blurSettings.blurTarget,
      blurSettings.edgePreserve,
      blurSettings.gaussianMix,
      lockBlurTarget,
      needsCutout,
      showGaussianControls,
    ]
  );

  const buildExport = useCallback(() => {
    const settings = {
      type: resolveBlurType(blurSettings.type, variant.allowedTypes),
      amount: clampBlurAmount(blurSettings.amount),
      cutoutUrl: revealCutoutUrl,
      ...pixelExportFields,
      ...gaussianExportFields,
    };
    return () => exportBlurPrepImageBlob(sourceImageUrl, settings);
  }, [
    blurSettings.amount,
    blurSettings.type,
    gaussianExportFields,
    pixelExportFields,
    revealCutoutUrl,
    sourceImageUrl,
    variant.allowedTypes,
  ]);

  const handleApplyBlur = async () => {
    if (!sourceImageUrl || blurBusy || !hasActiveBlurSettings(blurSettings.amount)) {
      return;
    }
    if (needsCutout && !revealCutoutUrl) return;
    await applyBlur(buildExport());
  };

  const handleAdjustBlur = () => {
    setBlurApplyError(null);
    resetApplied();
    setSelectedKey("preview");
  };

  const handleDone = async () => {
    setDoneBusy(true);
    try {
      if (blurApplied && blurPreviewUrl) {
        onDone({
          imageUrl: blurPreviewUrl,
          creationId: needsCutout ? creationId : null,
          effectBaked: true,
        });
        return;
      }

      if (!sourceImageUrl) {
        throw new Error("Upload an image first");
      }

      let readyCutout = revealCutoutUrl;
      let doneCreationId = creationId;
      if (needsCutout && !readyCutout) {
        const removed = await runBackgroundRemoval();
        readyCutout = removed?.cutoutUrl;
        doneCreationId = removed?.creationId ?? doneCreationId;
      }

      const blob = await exportBlurPrepImageBlob(sourceImageUrl, {
        type: resolveBlurType(blurSettings.type, variant.allowedTypes),
        amount: clampBlurAmount(blurSettings.amount),
        cutoutUrl: needsCutout ? readyCutout : null,
        ...pixelExportFields,
        ...gaussianExportFields,
      });
      const bakedUrl = URL.createObjectURL(blob);
      onDone({
        imageUrl: bakedUrl,
        creationId: needsCutout ? doneCreationId : null,
        effectBaked: true,
      });
    } catch (err) {
      setBgError(err?.message || "Could not finish blur setup");
    } finally {
      setDoneBusy(false);
    }
  };

  const viewingOriginal = selectedKey === "original";

  const canApplyBlur =
    Boolean(sourceImageUrl) &&
    !waitingOnCutout &&
    hasActiveBlurSettings(blurSettings.amount) &&
    !blurBusy;

  const configureFooterHint = !sourceImageUrl
    ? "Upload an image to start adding blur."
    : waitingOnCutout
      ? "Removing background…"
      : bgError
        ? bgError
        : !hasActiveBlurSettings(blurSettings.amount)
          ? "Increase the blur amount, then apply."
          : lockBackgroundTarget
            ? "Background is blurred; the subject stays sharp."
            : showGaussianControls
            ? blurTarget === "background"
              ? "Background is blurred; the subject stays sharp."
              : blurTarget === "subject"
                ? "Subject is blurred; the scene stays sharp."
                : "Blur applies to the whole image. Choose Subject or Background to isolate."
            : removeBg
              ? "Blur applies to the subject. Turn off Remove Background to blur the whole image."
              : "Blur applies to the whole image. Turn on Remove Background to blur the subject only.";

  const leftContent = (
    <div className="btp-panel">
      <div className="btp-source-block">
        <ToolPrepSourceCards
          selectedKey={selectedKey}
          onSelect={setSelectedKey}
          originalImageUrl={sourceImageUrl}
          previewImageUrl={blurApplied ? blurPreviewUrl : null}
          removeBgActive={needsCutout && Boolean(revealCutoutUrl)}
          cutoutPending={waitingOnCutout}
          onUploadClick={openUploadPicker}
          onRemove={clearSource}
          uploadProgress={uploadProgress}
          sourceName={sourceFileName}
          sourceSizeLabel={sourceFileSizeLabel}
          actionsDisabled={bgLoading || doneBusy || downloadBusy}
          hasResult={blurApplied}
          removeBgControls={
            showGaussianControls || lockBackgroundTarget
              ? null
              : {
                  active: removeBg,
                  onToggle: handleRemoveBgToggle,
                  disabled:
                    !sourceImageUrl || bgLoading || blurApplied || doneBusy,
                  bio: "On: blur the subject only. Off: blur the entire image.",
                  error: bgError,
                }
          }
        />
      </div>

      <div
        className={`btp-controls tool-prep-scroll${
          blurApplied ? " btp-controls--locked" : ""
        }`}
        ref={scrollBodyRef}
      >
        <BlurPrepControls
          toolKey={variant.toolKey}
          toolTitle={variant.title}
          settings={blurSettings}
          onChange={handleSettingsChange}
          disabled={!sourceImageUrl || blurApplied || bgLoading || doneBusy}
        />
      </div>
    </div>
  );

  useSyncScrollbarWidth(scrollBodyRef, [
    sourceImageUrl,
    cutoutUrl,
    needsCutout,
    blurApplied,
    blurSettings.type,
    blurSettings.amount,
    blurSettings.pixelShape,
    blurSettings.pixelGap,
    blurSettings.pixelSoftness,
    blurSettings.pixelMix,
    blurSettings.blurTarget,
    blurSettings.gaussianMix,
    blurSettings.edgePreserve,
    variant.toolKey,
  ]);

  return (
    <>
      <ImagePickerModal {...imagePickerProps} />
      <ToolPrepPopup
        toolKey={variant.toolKey}
        title={variant.title}
        howToUse={buildToolPrepHowToUse({
          aka: variant.aka,
          bio: variant.subtitle,
          steps: [
            "Upload an image",
            showGaussianControls
              ? "Choose Full, Subject, or Background, then adjust Amount / Mix / Edge preserve"
              : "Adjust blur settings on the left",
            `Tap ${variant.applyLabel || "Apply"} when ready`,
          ],
          tip: showGaussianControls
            ? "Subject and Background need a cutout — Remove Background runs automatically when those targets are selected."
            : "Turn on Remove Background to blur the subject only and keep the scene sharp.",
        })}
        left={leftContent}
        preview={
          !viewingOriginal && blurApplied && blurPreviewUrl ? (
            <ToolPrepFlatResultStage
              className="blur-tool-preview"
              resultImageUrl={blurPreviewUrl}
              applying={blurBusy}
              applyingLabel="Applying blur…"
              resultAlt={`${variant.title} result`}
              frameNaturalSize={frameNaturalSize}
              softWatermarkToolKey={variant.toolKey}
              emptyLabel={
                uploadProgress != null && !sourceImageUrl
                  ? TOOL_PREP_PREPARING_PREVIEW_LABEL
                  : undefined
              }
            />
          ) : (
            <BlurPreview
              imageUrl={sourceImageUrl}
              cutoutUrl={viewingOriginal ? null : revealCutoutUrl}
              type={blurSettings.type}
              amount={viewingOriginal ? 0 : blurSettings.amount}
              pixelShape={blurSettings.pixelShape}
              pixelGap={blurSettings.pixelGap}
              pixelSoftness={blurSettings.pixelSoftness}
              pixelMix={blurSettings.pixelMix}
              blurTarget={
                viewingOriginal ? "full" : gaussianExportFields.blurTarget
              }
              gaussianMix={viewingOriginal ? 0 : blurSettings.gaussianMix}
              edgePreserve={blurSettings.edgePreserve}
              forceCanvasPreview={
                viewingOriginal
                  ? false
                  : showGaussianControls || lockBackgroundTarget
              }
              loading={viewingOriginal ? false : waitingOnCutout}
              applying={viewingOriginal ? false : blurBusy}
              applyingLabel="Applying blur…"
              showSubjectBox={
                viewingOriginal ? false : Boolean(revealCutoutUrl)
              }
              emptyLabel={
                uploadProgress != null && !sourceImageUrl
                  ? TOOL_PREP_PREPARING_PREVIEW_LABEL
                  : variant.emptyPreview
              }
            />
          )
        }
        onDone={blurApplied ? handleDone : handleApplyBlur}
        onCancel={onCancel}
        confirmClose={Boolean(sourceImageUrl)}
        onDownload={blurApplied ? () => handleDownload(buildExport()) : null}
        onOpenInEditor={blurApplied ? handleDone : null}
        onSecondaryAction={blurApplied ? handleAdjustBlur : null}
        secondaryActionLabel={variant.adjustLabel}
        busy={blurBusy || doneBusy || waitingOnCutout || downloadBusy}
        busyLabel={waitingOnCutout ? "Removing background…" : "Applying blur…"}
        showDone={!blurApplied}
        doneDisabled={!canApplyBlur}
        doneLabel={variant.applyLabel}
        footerActionOrder={
          blurApplied
            ? ["cancel", "secondary", "download", "openInEditor"]
            : ["cancel", "primary"]
        }
        openInEditorLabel="Open in Editor"
        footerHint={
          blurApplyError ||
          (blurApplied ? TOOL_PREP_RESULT_FOOTER_HINT : configureFooterHint)
        }
        footerHintClassName={
          blurApplyError ? "tool-prep-popup__footer-hint--error" : ""
        }
      />
      <ToolPrepDownloadFlowModals
        flow={downloadFlow}
        toolKey={variant.toolKey}
        onPrimaryAction={() => {
          downloadFlow.handleThankYouDismiss();
          clearSource();
        }}
        onBackToDashboard={onCancel}
      />
    </>
  );
};

export default BlurToolPrep;
